import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NgxsModule } from '@ngxs/store';
import { CalendarModule, DateAdapter as CalendarDateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { GridsterModule } from 'angular-gridster2';
import moment from 'moment';
import { DateAdapter } from 'angular-calendar/date-adapters/date-adapter';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { NgxEditorModule } from 'ngx-editor';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { AngularResizeEventModule } from 'angular-resize-event';
import { CallerDetailsTooltipComponent } from '@nimbus-library/features/shared/components/caller-details-tooltip/caller-details-tooltip.component';
import { AdminConsentStatusComponent } from '@nimbus-library/features/shared/components/consent-status/admin-consent-status/admin-consent-status.component';
import { UserConsentStatusComponent } from '@nimbus-library/features/shared/components/consent-status/user-consent-status/user-consent-status.component';
import { ErrorScreenComponent } from '@nimbus-library/features/shared/components/error-screen/error-screen.component';
import { GoogleMapWidgetComponent } from '@nimbus-library/features/shared/components/google-map-widget/google-map-widget.component';
import { NimbusMaterialButtonModule } from '@nimbus-library/features/shared/components/material/buttons/nimbus-material-button.module';
import { StringSanitizerPipe } from '@nimbus-library/features/shared/components/material/core/pipes/string-sanitizer.pipe';
import { SecurityGroupInfoComponent } from '@nimbus-library/features/shared/components/material/input-administration-security-group/administration-group-info-dialog/security-group-info.component';
import { InputAdministrationSecurityGroupComponent } from '@nimbus-library/features/shared/components/material/input-administration-security-group/input-administration-security-group.component';
import { SecurityGroupService } from '@nimbus-library/features/shared/components/material/input-administration-security-group/security-group.service';
import { InputAssignProfileComponent } from '@nimbus-library/features/shared/components/material/input-assign-profile/input-assign-profile.component';
import { InputAssignSkillByUserComponent } from '@nimbus-library/features/shared/components/material/input-assign-skill/input-assign-skill-by-user.component';
import { InputAssignSkillComponent } from '@nimbus-library/features/shared/components/material/input-assign-skill/input-assign-skill.component';
import { SkillStepperModule } from '@nimbus-library/features/shared/components/material/input-assign-skill/skill-stepper/skill-stepper.module';
import { InputCheckBoxComponent } from '@nimbus-library/features/shared/components/material/input-check-box/input-check-box.component';
import { InputDatePickerHeaderComponent } from '@nimbus-library/features/shared/components/material/input-date-picker/input-date-picker-header/input-date-picker-header.component';
import { InputDatePickerComponent } from '@nimbus-library/features/shared/components/material/input-date-picker/input-date-picker.component';
import { InputDateTimePickerComponent } from '@nimbus-library/features/shared/components/material/input-date-time-picker/input-date-time-picker.component';
import { InputDndChipListComponent } from '@nimbus-library/features/shared/components/material/input-dnd-list/input-dnd-chip-list/input-dnd-chip-list.component';
import { InputDndInputListComponent } from '@nimbus-library/features/shared/components/material/input-dnd-list/input-dnd-input-list/input-dnd-input-list.component';
import { InputDropdownComponent } from '@nimbus-library/features/shared/components/material/input-dropdown/input-dropdown.component';
import { InputDropdownPopupComponent } from '@nimbus-library/features/shared/components/material/input-dropdown/input-dropdown-popup.component';
import { InputEmailComponent } from '@nimbus-library/features/shared/components/material/input-email/input-email.component';
import { InputEmailListComponent } from '@nimbus-library/features/shared/components/material/input-email-list/input-email-list.component';
import { InputLabelComponent } from '@nimbus-library/features/shared/components/material/input-label/input-label.component';
import { InputMediaPlayerComponent } from '@nimbus-library/features/shared/components/material/input-media-player/input-media-player.component';
import { InputNumberComponent } from '@nimbus-library/features/shared/components/material/input-number/input-number.component';
import { InputParametersOverlayComponent } from '@nimbus-library/features/shared/components/material/input-parameters-overlay/input-parameters-overlay.component';
import { InputPhoneNumberComponent } from '@nimbus-library/features/shared/components/material/input-phone-number/input-phone-number.component';
import { InputRadioGroupComponent } from '@nimbus-library/features/shared/components/material/input-radio-group/input-radio-group.component';
import { InputSearchComponent } from '@nimbus-library/features/shared/components/material/input-search/input-search.component';
import { InputSlideToggleComponent } from '@nimbus-library/features/shared/components/material/input-slide-toggle/input-slide-toggle.component';
import { InputStringComponent } from '@nimbus-library/features/shared/components/material/input-string/input-string.component';
import { InputTagListComponent } from '@nimbus-library/features/shared/components/material/input-tag-list/input-tag-list.component';
import { InputTextAreaAutosizeDirective } from '@nimbus-library/features/shared/components/material/input-text-area/input-text-area-autosize.directive';
import { InputTextAreaComponent } from '@nimbus-library/features/shared/components/material/input-text-area/input-text-area.component';
import { InputTimePickerComponent } from '@nimbus-library/features/shared/components/material/input-time-picker/input-time-picker.component';
import { InputTokenizedTextAreaComponent } from '@nimbus-library/features/shared/components/material/input-tokenized-text-area/input-tokenized-text-area.component';
import { InputUploadAudiofileComponent } from '@nimbus-library/features/shared/components/material/input-upload-audiofile/input-upload-audiofile.component';
import { InputUploadImageComponent } from '@nimbus-library/features/shared/components/material/input-upload-image/input-upload-image.component';
import { InputUserAutocompleteComponent } from '@nimbus-library/features/shared/components/material/input-user-autocomplete/input-user-autocomplete.component';
import { MatDndChipListComponent } from '@nimbus-library/features/shared/components/material/mat-dnd-list/mat-dnd-chip-list/mat-dnd-chip-list.component';
import { MatDndInputListComponent } from '@nimbus-library/features/shared/components/material/mat-dnd-list/mat-dnd-input-list/mat-dnd-input-list.component';
import { MatDndInputDirective } from '@nimbus-library/features/shared/components/material/mat-dnd-list/mat-dnd-input-list/mat-dnd-input.directive';
import { MatDropDownModule } from '@nimbus-library/features/shared/components/material/mat-drop-down/mat-drop-down.module';
import { MatIconOverlayComponent } from '@nimbus-library/features/shared/components/material/mat-icon-overlay/mat-icon-overlay.component';
import { MatSelectComponent } from '@nimbus-library/features/shared/components/material/mat-select/mat-select.component';
import { MatMultipleSelectComponent } from '@nimbus-library/features/shared/components/material/mat-multiple-select/mat-multiple-select.component';
import { MaterialTabComponent } from '@nimbus-library/features/shared/components/material/mat-tabs/material-tab/material-tab.component';
import { MaterialTabsWrapperComponent } from '@nimbus-library/features/shared/components/material/mat-tabs/material-tabs-wrapper.component';
import { MatTagListComponent } from '@nimbus-library/features/shared/components/material/mat-tag-list/mat-tag-list.component';
import { MatTimeModule } from '@nimbus-library/features/shared/components/material/mat-time/mat-time.module';
import { FormControlDoubleSizeDirective } from '@nimbus-library/features/shared/components/material/material-form';
import { FormActionBarComponent } from '@nimbus-library/features/shared/components/material/material-form/form-action-bar/form-action-bar.component';
import { FormControlComponent } from '@nimbus-library/features/shared/components/material/material-form/form-control/form-control.component';
import { FormRowComponent } from '@nimbus-library/features/shared/components/material/material-form/form-row/form-row.component';
import { FormVerticalIntervalComponent } from '@nimbus-library/features/shared/components/material/material-form/form-small-vertical-interval/form-vertical-interval.component';
import { FormWrapperComponent } from '@nimbus-library/features/shared/components/material/material-form/form-wrapper/form-wrapper.component';
import { MaterialImageComponent } from '@nimbus-library/features/shared/components/material/material-image/material-image.component';
import { SectionWrapperComponent } from '@nimbus-library/features/shared/components/material/material-section/section-wrapper/section-wrapper.component';
import { NimbusMaterialConfirmPopupComponent } from '@nimbus-library/features/shared/components/material/modals/nimbus-material-confirm-popup/nimbus-material-confirm-popup.component';
import { NimbusMaterialInformationPopupComponent } from '@nimbus-library/features/shared/components/material/modals/nimbus-material-information-popup/nimbus-material-information-popup.component';
import { NimbusMaterialUnsavedChangesPopupComponent } from '@nimbus-library/features/shared/components/material/modals/nimbus-material-unsaved-changes-popup/nimbus-material-unsaved-changes-popup.component';
import { NimbusMaterialPageModule } from '@nimbus-library/features/shared/components/material/nimbus-page/nimbus-material-page.module';
import { NimbusDualListBoxComponent } from '@nimbus-library/features/shared/components/nimbus-dual-list-box/nimbus-dual-list-box.component';
import { NimbusPaginationComponent } from '@nimbus-library/features/shared/components/nimbus-pagination/nimbus-pagination.component';
import { SliderModule } from '@nimbus-library/features/shared/components/slider/slider.module';
import {
  NimbusButtonGroupDirective,
  NimbusButtonGroupElementDirective,
  NimbusSortDirective,
  NimbusSortHeaderDirective,
} from '@nimbus-library/features/shared/directives';
import { CopyClipboardDirective } from '@nimbus-library/features/shared/directives/copy-clipboard.directive';
import { DropFileDirective } from '@nimbus-library/features/shared/directives/drop-file.directive';
import { NimbusAfterViewInitDirective } from '@nimbus-library/features/shared/directives/nimbus-after-view-init.directive';
import { NimbusCheckboxV2Directive } from '@nimbus-library/features/shared/directives/nimbus-checkbox-v2.directive';
import { NimbusInputCounterDirective } from '@nimbus-library/features/shared/directives/nimbus-input-counter.directive';
import { NimbusInputValidationDirective } from '@nimbus-library/features/shared/directives/nimbus-input-validation.directive';
import { NimbusShowMoreDirective } from '@nimbus-library/features/shared/directives/nimbus-show-more.directive';
import { NimbusMutationObserverDirective } from '@nimbus-library/features/shared/directives/nimbus-size-mutation-observer.directive';
import { NimbusThemeDirective } from '@nimbus-library/features/shared/directives/nimbus-theme.directive';
import { ValidGuidDirective } from '@nimbus-library/features/shared/directives/valid-guid.directive';
import { ValidNumberDirective } from '@nimbus-library/features/shared/directives/valid-number.directive';
import { ValidPhoneNumberDirective } from '@nimbus-library/features/shared/directives/valid-phone-number.directive';
import { AppConfiguration } from '@nimbus-library/features/shared/services/base-http/app.config';
import { APP_CONFIG } from '@nimbus-library/features/shared/services/base-http/app.configuration';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { SharedHttpService } from '@nimbus-library/features/shared/services/base-http/http.service';
import { GoogleMapApiClient } from '@nimbus-library/features/shared/services/google-map/google-map.api-client';
import { GoogleMapService } from '@nimbus-library/features/shared/services/google-map/google-map.service';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';
import { NimbusGroupSelectionComponent } from '@nimbus-library/features/shared/components/nimbus-dropdown-v2/nimbus-group-selection.component';
import { InputDropdownDefaultSearchDirective } from '@nimbus-library/features/shared/components/material/input-dropdown/input-dropdown-default-search.directive';
import { CdkConnectedOverlayDelayDirective } from '@nimbus-library/features/shared/components/material/overlay/cdk-connected-overlay-delay.directive';
import { SafePipe } from '@nimbus-library/features/shared/pipes/safe.pipe';
import { NimbusFilterGroupComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-group/nimbus-filter-group.component';
import { NimbusFilterSelectComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select/nimbus-filter-select.component';
import { NimbusFilterMenuComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-menu/nimbus-filter-menu.component';
import { NimbusFilterSummaryButtonComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-summary-button/nimbus-filter-summary-button.component';
import { NimbusFilterSelectListComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-list/nimbus-filter-select-list.component';
import { NimbusFilterSelectListItemComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-list-item/nimbus-filter-select-list-item.component';
import { NimbusFilterDurationRangeComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-duration-range/nimbus-filter-duration-range.component';
import { NimbusFilterDurationRangeInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-duration-range-input/nimbus-filter-duration-range-input.component';
import { NimbusFilterDatetimeRangeComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-datetime-range/nimbus-filter-datetime-range.component';
import { NimbusFilterDatetimeRangeInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-datetime-range-input/nimbus-filter-datetime-range-input.component';
import { SpinnerModule } from '@nimbus-library/features/shared/components/spinner/spinner.module';
import { NimbusFilterSearchComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-search/nimbus-filter-search.component';
import { NimbusFilterSearchListComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-search-list/nimbus-filter-search-list.component';
import { NimbusFilterTextComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-text/nimbus-filter-text.component';
import { NimbusFilterTextInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-text-input/nimbus-filter-text-input.component';
import { NimbusFilterDateRangeInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-date-range-input/nimbus-filter-date-range-input.component';
import { NimbusFilterTimeRangeInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-time-range-input/nimbus-filter-time-range-input.component';
import { InputAutocompleteComponent } from '@nimbus-library/features/shared/components/material/input-autocomplete/input-autocomplete.component';
import { InputAutocompleteDefaultSearchDirective } from '@nimbus-library/features/shared/components/material/input-autocomplete/input-autocomplete-default-search.directive';
import { NimbusFilterHierarchySelectComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-hierarchy-select/nimbus-filter-hierarchy-select.component';
import { NimbusFilterHierarchySelectListComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-hierarchy-select-list/nimbus-filter-hierarchy-select-list.component';
import { NimbusFilterHierarchySelectListItemComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-hierarchy-list-item/nimbus-filter-hierarchy-select-list-item.component';
import { InputOrganizationUnitsComponent } from '@nimbus-library/features/shared/components/material/input-organization-units/input-organization-units.component';
import { NimbusFilterSummaryInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-summary-input/nimbus-filter-summary-input.component';
import { HistoryStatusIndicatorComponent } from '@nimbus-library/features/shared/components/history-status-indicator/history-status-indicator.component';
import { DateSinceDateFormatPipe } from '@nimbus-library/features/shared/pipes/date-since-format.pipe';
import { TruncatePipe } from '@nimbus-library/features/shared/pipes/truncate.pipe';
import { InputAutocompleteDefaultHighlightDirective } from '@nimbus-library/features/shared/components/material/input-autocomplete/input-autocomplete-default-highlight.directive';
import { InputRichTextComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text.component';
import { InputRichTextMenuComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/input-rich-text-menu.component';
import { ButtonToggleGroupComponent } from '@nimbus-library/features/shared/components/button-toggle-group/button-toggle-group.component';
import { SortToggleComponent } from '@nimbus-library/features/shared/components/sort-toggle/sort-toggle.component';
import { FormattingToggleGroupComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/formatting-toggle-group/formatting-toggle-group.component';
import { AlignmentToggleGroupComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/alignment-toggle-group/alignment-toggle-group.component';
import { ClearFormattingButtonComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/clear-formatting-button/clear-formatting-button.component';
import { IconButtonGroupComponent } from '@nimbus-library/features/shared/components/icon-button-group/icon-button-group.component';
import { UserCapabilitiesComponent } from '@nimbus-library/features/shared/components/components/user-capabilities.component';
import { TaskCapacityPercentageInfoComponent } from '@nimbus-global/features/user-tasks-capacity/components/task-capacity-percentage-info/task-capacity-percentage-info.component';
import { UserInputLicenseModalityCheckboxItemComponent } from '@nimbus-library/features/shared/components/material/input-license/user-input-license-modality-checkbox-item/user-input-license-modality-checkbox-item.component';
import { UserInputLicenseCheckboxItemComponent } from '@nimbus-library/features/shared/components/material/input-license/user-input-license-checkbox-item/user-input-license-checkbox-item.component';
import { UserInputLicenseCheckboxGroupComponent } from '@nimbus-library/features/shared/components/material/input-license/user-input-license-checkbox-group/user-input-license-checkbox-group.component';
import { ServiceInputLicenseRadioButtonItemComponent } from '@nimbus-library/features/shared/components/material/input-license/service-input-license-radio-button-item/service-input-license-radio-button-item.component';
import { ServiceInputLicenseRadioButtonGroupComponent } from '@nimbus-library/features/shared/components/material/input-license/service-input-license-radio-button-group/service-input-license-radio-button-group.component';
import { ServiceInputLicenseCheckBoxItemComponent } from '@nimbus-library/features/shared/components/material/input-license/service-input-license-check-box-item/service-input-license-check-box-item.component';
import { ServiceLicenseCheckBoxGroupComponent } from '@nimbus-library/features/shared/components/material/input-license/service-input-license-addon-group/service-license-check-box-group.component';
import { NimbusExternalLinksDirective } from '@nimbus-library/features/shared/directives/nimbus-external-links.directive';
import { ListsAndIndentToggleGroupComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/lists-and-indent-toggle-group/lists-and-indent-toggle-group.component';
import { ColorOverlayComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/color-overlay/color-overlay.component';
import { FontColorOverlayComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/font-color-overlay/font-color-overlay.component';
import { BackgroundColorOverlayComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/background-color-overlay/background-color-overlay.component';
import { FontSizeMenuItemComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/font-size-menu-item/font-size-menu-item.component';
import { FontSizeSelectionComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/font-size-menu-item/font-size-selection/font-size-selection.component';
import { NimbusMouseDownEventBlockerDirective } from '@nimbus-library/features/shared/directives/nimbus-mouse-down-event-blocker.directive';
import { FontFamilySelectionComponent } from '@nimbus-library/features/shared/components/material/input-rich-text/input-rich-text-menu/font-family-selection/font-family-selection.component';
import { CallLinkDirective } from '@nimbus-library/features/shared/directives/call-link.directive';
import { InputAutocompletePositioningDirective } from '@nimbus-library/features/shared/components/material/input-autocomplete/input-autocomplete-positioning.directive';
import { InputColoredAutocompleteComponent } from '@nimbus-library/features/shared/components/material/input-colored-autocomplete/input-colored-autocomplete.component';
import { InputOrganizationUnitAutocompleteComponent } from '@nimbus-library/features/shared/components/material/input-organization-unit-autocomplete/input-organization-unit-autocomplete.component';
import { InformationWidgetComponent } from '@nimbus-library/features/shared/components/information-widget/information-widget.component';
import { JoinPipe } from '@nimbus-library/features/shared/pipes/join.pipe';
import { NimbusFilterSummaryInputMaskComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-summary-input/nimbus-filter-summary-input-mask/nimbus-filter-summary-input-mask.component';
import { InputEmergencyDestinationComponent } from '@nimbus-library/features/shared/components/material/input-emergency-destination/input-emergency-destination.component';
import { NimbusContextMenuTriggerDirective } from '@nimbus-library/features/shared/directives/mat-context-menu-trigger.directive';
import { InputJsonPathMapListComponent } from '@nimbus-library/features/shared/components/material/input-json-path-map-list/input-json-path-map-list.component';
import { MatJsonPathMapListComponent } from '@nimbus-library/features/shared/components/material/mat-grid-list/mat-json-path-map-list/mat-json-path-map-list.component';
import { InputStringListComponent } from '@nimbus-library/features/shared/components/material/input-string-list/input-string-list.component';
import { LicenseBreachBannerComponent } from '@nimbus-library/features/shared/components/license-breach-banner/license-breach-banner.component';
import { NimbusInfoBannerComponent } from '@nimbus-library/features/shared/components/nimbus-info-banner/nimbus-info-banner.component';
import { ResizableDividerComponent } from '@nimbus-library/features/shared/components/resizable-divider/resizable-divider.component';
import { VirtualUserLicenseCheckBoxGroupComponent } from '@nimbus-library/features/shared/components/material/input-license/virtual-user/virtual-user-input-license-group/virtual-user-license-check-box-group.component';
import { VirtualUserInputLicenseCheckboxItemComponent } from '@nimbus-library/features/shared/components/material/input-license/virtual-user/virtual-user-input-license-checkbox-item/virtual-user-input-license-checkbox-item.component';
import { InputPermissionsFieldDescriptorAssertDirective } from '@nimbus-global/features/permissions/directives/permissions-field-assert.directive';
import { LocalizationModule } from '../localization/localization.module';
import { InputTimePickerByDurationComponent } from './components/material/input-time-picker-by-duration/input-time-picker-by-duration.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { CollapsibleSidebarPanelComponent } from './components/collapsible-sidebar-panel/collapsible-sidebar-panel.component';
import { ButtonGroupComponent } from './components/button-group/button-group.component';
import { CalendarDayHeaderComponent } from './components/calendar-day-header/calendar-day-header.component';
import { CalendarHeaderComponent } from './components/calendar-header/calendar-header.component';
import { CalendarComponent } from './components/calendar/calendar.component';
import { OrganizationUnitToTagPipe } from './components/material/input-assign-role/domain/ou-to-tag.pipe';
import { InputAssignRoleComponent } from './components/material/input-assign-role/input-assign-role.component';
import { InputAssignServiceUserComponent } from './components/material/input-assign-service-user/input-assign-service-user.component';
import { InputDndEditableListComponent } from './components/material/input-dnd-list/input-dnd-editable-list/input-dnd-editable-list.component';
import { InputParamsListComponent } from './components/material/input-grid-list/input-params-list/input-params-list.component';
import { InputUrisListComponent } from './components/material/input-grid-list/input-uris-list.component';
import { InputOrganizationUnitComponent } from './components/material/input-organization-unit/input-organization-unit.component';
import { InputPasswordComponent } from './components/material/input-password/input-password.component';
import { InputTagSelectorComponent } from './components/material/input-tag-selector/input-tag-selector.component';
import { DependentParametersTextAreaComponent } from './components/material/input-tokenized-text-area/input-dependent-tokenized-text-area.component';
import { LinkComponent } from './components/material/link/link.component';
import { MatDndEditableListComponent } from './components/material/mat-dnd-list/mat-dnd-editable-list/mat-dnd-editable-list.component';
import { BaseMatGridComponent } from './components/material/mat-grid-list/base-mat-grid.component';
import { MatHeaderListComponent } from './components/material/mat-grid-list/mat-headers-list/mat-header-list.component';
import { MatUrisListComponent } from './components/material/mat-grid-list/mat-uris-list/mat-uris-list.component';
import { NimbusCardComponent } from './components/nimbus-card/nimbus-card.component';
import { NimbusCopyLinkComponent } from './components/nimbus-copy-link/nimbus-copy-link.component';
import { NimbusGridModule } from './components/nimbus-grid/nimbus-grid.module';
import { NimbusIconComponent } from './components/nimbus-icon/nimbus-icon.component';
import { NimbusInputCounterComponent } from './components/nimbus-input-counter/nimbus-input-counter.component';
import { NimbusTagListComponent } from './components/nimbus-tag-list/nimbus-tag-list.component';
import { OverlayEntityDetailsComponent } from './components/overlay-entity/overlay-entity-details.component';
import { OverlayListboxComponent } from './components/overlay-listbox/overlay-listbox.component';
import { ReportProblemModalComponent } from './components/report-problem-modal/report-problem-modal.component';
import { IntersectionObserverDirective } from './directives/intersection-observer.directive';
import { MouseEventBlockerDirective } from './directives/mouse-event-blocker.directive';
import { NimbusAvatarDirective } from './directives/nimbus-avatar.directive';
import { NimbusFittextDirective } from './directives/nimbus-fittext.directive';
import { ValidNimbusDomainsListDirective } from './directives/valid-domains-list.directive';
import { NimbusIconModule } from './nimbus-icon.module';
import { NimbusTooltipModule } from './nimbus-tooltip.module';
import { DateDifferencePipe } from './pipes/date-difference.pipe';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { DateIsTodayPipe } from './pipes/date-is-today.pipe';
import { DurationFormatPipe } from './pipes/duration-format.pipe';
import { InitialsPipe } from './pipes/initials.pipe';
import { SelectPipe } from './pipes/select.pipe';
import { NimbusInputTrimDirective } from './directives/nimbus-input-trim.directive';
import { NimbusBulkValueDirective } from './directives/functional/nimbus-bulk-value.directive';
import { NimbusFilterNumberRangeComponent } from './components/nimbus-grid-filter/nimbus-filter-number-range/nimbus-filter-number-range.component';
import { NimbusFilterNumberRangeInputComponent } from './components/nimbus-grid-filter/nimbus-filter-number-range-input/nimbus-filter-number-range-input.component';

export function momentAdapterFactory(): DateAdapter {
  return adapterFactory(moment);
}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
    RouterModule,
    GridsterModule,
    SliderModule,
    A11yModule,
    DragDropModule,
    OverlayModule,
    LocalizationModule,
    NgxsModule.forFeature([CommonThemeState]),
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDialogModule,
    MatCheckboxModule,
    MatChipsModule,
    MatRadioModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatDropDownModule,
    MatTimeModule,
    MatTooltipModule,
    NimbusGridModule,
    NimbusIconModule,
    NimbusTooltipModule,
    NimbusMaterialButtonModule,
    NimbusMaterialPageModule,
    SkillStepperModule,
    CalendarModule.forRoot({
      provide: CalendarDateAdapter,
      useFactory: momentAdapterFactory,
    }),
    MatMenuModule,
    A11yModule,
    SpinnerModule,
    NgxEditorModule,
    MatButtonToggleModule,
    TaskCapacityPercentageInfoComponent,
    AngularResizeEventModule,
    InputPermissionsFieldDescriptorAssertDirective,
  ],
  declarations: [
    NimbusFittextDirective,
    NimbusCardComponent,
    ButtonGroupComponent,
    ButtonToggleGroupComponent,
    SortToggleComponent,
    CopyClipboardDirective,
    DateFormatPipe,
    DateDifferencePipe,
    DurationFormatPipe,
    DateIsTodayPipe,
    DateSinceDateFormatPipe,
    DropFileDirective,
    AvatarComponent,
    CollapsibleSidebarPanelComponent,
    NimbusAvatarDirective,
    NimbusThemeDirective,
    NimbusGroupSelectionComponent,
    ErrorScreenComponent,
    MouseEventBlockerDirective,
    NimbusBulkValueDirective,
    NimbusButtonGroupDirective,
    NimbusButtonGroupElementDirective,
    NimbusSortDirective,
    NimbusSortHeaderDirective,
    NimbusDualListBoxComponent,
    NimbusPaginationComponent,
    NimbusCheckboxV2Directive,
    NimbusShowMoreDirective,
    NimbusInputCounterDirective,
    NimbusInputTrimDirective,
    NimbusInputValidationDirective,
    ValidPhoneNumberDirective,
    ValidGuidDirective,
    ValidNumberDirective,
    NimbusMutationObserverDirective,
    NimbusAfterViewInitDirective,
    AdminConsentStatusComponent,
    UserConsentStatusComponent,
    NimbusIconComponent,
    NimbusInputCounterComponent,
    NimbusCopyLinkComponent,
    GoogleMapWidgetComponent,
    CallerDetailsTooltipComponent,
    OverlayListboxComponent,
    IntersectionObserverDirective,
    OverlayEntityDetailsComponent,
    ValidNimbusDomainsListDirective,
    InputAdministrationSecurityGroupComponent,
    InputStringComponent,
    InputStringListComponent,
    InputSearchComponent,
    InputEmailComponent,
    InputEmailListComponent,
    InputLabelComponent,
    InputNumberComponent,
    InputPhoneNumberComponent,
    InputDropdownComponent,
    InputDropdownPopupComponent,
    InputUserAutocompleteComponent,
    InputOrganizationUnitComponent,
    InputOrganizationUnitAutocompleteComponent,
    InputOrganizationUnitsComponent,
    InputColoredAutocompleteComponent,
    ServiceInputLicenseCheckBoxItemComponent,
    ServiceInputLicenseRadioButtonGroupComponent,
    ServiceInputLicenseRadioButtonItemComponent,
    VirtualUserLicenseCheckBoxGroupComponent,
    VirtualUserInputLicenseCheckboxItemComponent,
    ServiceLicenseCheckBoxGroupComponent,
    UserInputLicenseCheckboxGroupComponent,
    UserInputLicenseCheckboxItemComponent,
    UserInputLicenseModalityCheckboxItemComponent,
    InputTagSelectorComponent,
    InputSlideToggleComponent,
    InputTextAreaComponent,
    InputTextAreaAutosizeDirective,
    FormActionBarComponent,
    FormWrapperComponent,
    FormRowComponent,
    FormControlComponent,
    FormControlDoubleSizeDirective,
    FormVerticalIntervalComponent,
    SectionWrapperComponent,
    InputUploadAudiofileComponent,
    InputMediaPlayerComponent,
    InputTokenizedTextAreaComponent,
    DependentParametersTextAreaComponent,
    InputUploadImageComponent,
    InputTimePickerComponent,
    InputTimePickerByDurationComponent,
    InputDatePickerComponent,
    InputDatePickerHeaderComponent,
    InputDateTimePickerComponent,
    InputCheckBoxComponent,
    InputRadioGroupComponent,
    InputRichTextComponent,
    InputRichTextMenuComponent,
    FormattingToggleGroupComponent,
    AlignmentToggleGroupComponent,
    ListsAndIndentToggleGroupComponent,
    ColorOverlayComponent,
    FontColorOverlayComponent,
    BackgroundColorOverlayComponent,
    FontSizeMenuItemComponent,
    FontSizeSelectionComponent,
    FontFamilySelectionComponent,
    InputParametersOverlayComponent,
    MatDndChipListComponent,
    MatDndInputListComponent,
    MatDndInputDirective,
    MatTagListComponent,
    MaterialTabsWrapperComponent,
    MaterialTabComponent,
    MatSelectComponent,
    MatMultipleSelectComponent,
    InputDndChipListComponent,
    InputDndInputListComponent,
    InputAssignSkillComponent,
    InputAssignSkillByUserComponent,
    InputTagListComponent,
    InputPasswordComponent,
    MatIconOverlayComponent,
    NimbusMaterialUnsavedChangesPopupComponent,
    NimbusMaterialConfirmPopupComponent,
    NimbusMaterialInformationPopupComponent,
    SecurityGroupInfoComponent,
    MatUrisListComponent,
    InputUrisListComponent,
    MatDndEditableListComponent,
    InputDndEditableListComponent,
    InputParamsListComponent,
    MatHeaderListComponent,
    BaseMatGridComponent,
    MaterialImageComponent,
    InitialsPipe,
    InputAssignServiceUserComponent,
    ReportProblemModalComponent,
    CalendarComponent,
    CalendarHeaderComponent,
    CalendarDayHeaderComponent,
    StringSanitizerPipe,
    LinkComponent,
    InputAssignRoleComponent,
    InputAssignProfileComponent,
    NimbusTagListComponent,
    SafePipe,
    SelectPipe,
    OrganizationUnitToTagPipe,
    InputDropdownDefaultSearchDirective,
    CdkConnectedOverlayDelayDirective,
    NimbusFilterGroupComponent,
    NimbusFilterSelectComponent,
    NimbusFilterSelectListComponent,
    NimbusFilterSelectListItemComponent,
    NimbusFilterMenuComponent,
    NimbusFilterSummaryButtonComponent,
    NimbusFilterSummaryInputComponent,
    NimbusFilterSummaryInputMaskComponent,
    NimbusFilterDurationRangeComponent,
    NimbusFilterDurationRangeInputComponent,
    NimbusFilterDatetimeRangeComponent,
    NimbusFilterDatetimeRangeInputComponent,
    NimbusFilterSearchComponent,
    NimbusFilterSearchListComponent,
    NimbusFilterTextComponent,
    NimbusFilterTextInputComponent,
    NimbusFilterDateRangeInputComponent,
    NimbusFilterNumberRangeComponent,
    NimbusFilterNumberRangeInputComponent,
    NimbusFilterTimeRangeInputComponent,
    NimbusFilterHierarchySelectComponent,
    NimbusFilterHierarchySelectListComponent,
    NimbusFilterHierarchySelectListItemComponent,
    InputAutocompleteComponent,
    InputAutocompleteDefaultSearchDirective,
    InputAutocompleteDefaultHighlightDirective,
    InputAutocompletePositioningDirective,
    HistoryStatusIndicatorComponent,
    TruncatePipe,
    UserCapabilitiesComponent,
    ClearFormattingButtonComponent,
    IconButtonGroupComponent,
    NimbusExternalLinksDirective,
    NimbusMouseDownEventBlockerDirective,
    CallLinkDirective,
    InformationWidgetComponent,
    JoinPipe,
    InputEmergencyDestinationComponent,
    NimbusContextMenuTriggerDirective,
    InputJsonPathMapListComponent,
    MatJsonPathMapListComponent,
    LicenseBreachBannerComponent,
    NimbusInfoBannerComponent,
    ResizableDividerComponent,
  ],
  exports: [
    NimbusFittextDirective,
    NimbusCardComponent,
    ButtonGroupComponent,
    ButtonToggleGroupComponent,
    SortToggleComponent,
    ErrorScreenComponent,
    GridsterModule,
    CopyClipboardDirective,
    DateFormatPipe,
    DateDifferencePipe,
    DurationFormatPipe,
    DateIsTodayPipe,
    DateSinceDateFormatPipe,
    DropFileDirective,
    AvatarComponent,
    CollapsibleSidebarPanelComponent,
    NimbusAvatarDirective,
    NimbusThemeDirective,
    NimbusGroupSelectionComponent,
    MouseEventBlockerDirective,
    NimbusBulkValueDirective,
    NimbusButtonGroupDirective,
    NimbusButtonGroupElementDirective,
    NimbusSortDirective,
    NimbusSortHeaderDirective,
    NimbusDualListBoxComponent,
    NimbusPaginationComponent,
    NimbusCheckboxV2Directive,
    NimbusShowMoreDirective,
    NimbusInputCounterDirective,
    NimbusInputValidationDirective,
    ValidPhoneNumberDirective,
    ValidGuidDirective,
    ValidNumberDirective,
    NimbusMutationObserverDirective,
    NimbusAfterViewInitDirective,
    A11yModule,
    DragDropModule,
    SliderModule,
    OverlayModule,
    AdminConsentStatusComponent,
    UserConsentStatusComponent,
    NimbusIconComponent,
    NimbusInputCounterComponent,
    NimbusCopyLinkComponent,
    GoogleMapWidgetComponent,
    CallerDetailsTooltipComponent,
    OverlayListboxComponent,
    IntersectionObserverDirective,
    OverlayEntityDetailsComponent,
    ValidNimbusDomainsListDirective,
    InputAdministrationSecurityGroupComponent,
    InputTagSelectorComponent,
    InputStringComponent,
    InputStringListComponent,
    InputSearchComponent,
    InputEmailComponent,
    InputEmailListComponent,
    InputLabelComponent,
    InputNumberComponent,
    InputParametersOverlayComponent,
    InputPhoneNumberComponent,
    InputDropdownComponent,
    InputDropdownPopupComponent,
    InputUserAutocompleteComponent,
    InputOrganizationUnitComponent,
    InputOrganizationUnitAutocompleteComponent,
    InputOrganizationUnitsComponent,
    InputColoredAutocompleteComponent,
    ServiceInputLicenseCheckBoxItemComponent,
    ServiceInputLicenseRadioButtonGroupComponent,
    ServiceInputLicenseRadioButtonItemComponent,
    VirtualUserLicenseCheckBoxGroupComponent,
    ServiceLicenseCheckBoxGroupComponent,
    UserInputLicenseCheckboxGroupComponent,
    UserInputLicenseCheckboxItemComponent,
    UserInputLicenseModalityCheckboxItemComponent,
    InputSlideToggleComponent,
    InputTextAreaComponent,
    FormActionBarComponent,
    FormWrapperComponent,
    FormRowComponent,
    FormControlComponent,
    FormControlDoubleSizeDirective,
    FormVerticalIntervalComponent,
    SectionWrapperComponent,
    InputUploadAudiofileComponent,
    InputMediaPlayerComponent,
    InputTokenizedTextAreaComponent,
    DependentParametersTextAreaComponent,
    InputUploadImageComponent,
    InputTimePickerComponent,
    InputTimePickerByDurationComponent,
    InputDatePickerComponent,
    InputDateTimePickerComponent,
    InputCheckBoxComponent,
    InputRadioGroupComponent,
    InputRichTextComponent,
    MatIconOverlayComponent,
    InputDndChipListComponent,
    InputDndInputListComponent,
    InputAssignSkillComponent,
    InputAssignSkillByUserComponent,
    InputTagListComponent,
    InputPasswordComponent,
    MaterialTabsWrapperComponent,
    MaterialTabComponent,
    MatSelectComponent,
    MatMultipleSelectComponent,
    NimbusMaterialUnsavedChangesPopupComponent,
    NimbusMaterialConfirmPopupComponent,
    NimbusMaterialInformationPopupComponent,
    NimbusMaterialButtonModule,
    MatUrisListComponent,
    InputUrisListComponent,
    MatDndEditableListComponent,
    InputDndEditableListComponent,
    InputParamsListComponent,
    MatHeaderListComponent,
    BaseMatGridComponent,
    NimbusMaterialPageModule,
    NimbusGridModule,
    NimbusIconModule,
    NimbusTooltipModule,
    MaterialImageComponent,
    InitialsPipe,
    InputAssignServiceUserComponent,
    CalendarComponent,
    CalendarHeaderComponent,
    CalendarDayHeaderComponent,
    LinkComponent,
    InputAssignRoleComponent,
    InputAssignProfileComponent,
    NimbusTagListComponent,
    SafePipe,
    SelectPipe,
    OrganizationUnitToTagPipe,
    MatRadioModule,
    InputDropdownDefaultSearchDirective,
    CdkConnectedOverlayDelayDirective,
    NimbusFilterGroupComponent,
    NimbusFilterSelectComponent,
    NimbusFilterMenuComponent,
    NimbusFilterSummaryButtonComponent,
    NimbusFilterSummaryInputComponent,
    NimbusFilterDurationRangeComponent,
    NimbusFilterDurationRangeInputComponent,
    NimbusFilterDatetimeRangeComponent,
    NimbusFilterDateRangeInputComponent,
    NimbusFilterNumberRangeComponent,
    NimbusFilterNumberRangeInputComponent,
    NimbusFilterTimeRangeInputComponent,
    NimbusFilterHierarchySelectComponent,
    NimbusFilterHierarchySelectListComponent,
    NimbusFilterHierarchySelectListItemComponent,
    SpinnerModule,
    InputDatePickerHeaderComponent,
    InputAutocompleteComponent,
    InputAutocompleteDefaultSearchDirective,
    InputAutocompleteDefaultHighlightDirective,
    InputAutocompletePositioningDirective,
    HistoryStatusIndicatorComponent,
    UserCapabilitiesComponent,
    NimbusExternalLinksDirective,
    NimbusMouseDownEventBlockerDirective,
    CallLinkDirective,
    TruncatePipe,
    IconButtonGroupComponent,
    InformationWidgetComponent,
    JoinPipe,
    InputEmergencyDestinationComponent,
    NimbusContextMenuTriggerDirective,
    InputJsonPathMapListComponent,
    MatJsonPathMapListComponent,
    LicenseBreachBannerComponent,
    NimbusInfoBannerComponent,
    ResizableDividerComponent,
  ],
  providers: [
    { provide: APP_CONFIG, useValue: AppConfiguration },
    { provide: BaseHttpService, useClass: SharedHttpService },
    SecurityGroupService,
    GoogleMapService,
    GoogleMapApiClient,
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
  ],
})
export class SharedUIModule {}
